"use client";

import Link from "next/link";
import { Menu, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import React from "react";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";

const menuItems = [
  { name: "Features", href: "#features" },
  { name: "How It Works", href: "#how-it-works" },
  { name: "Scenarios", href: "#scenarios" },
];

export const LandingHeader = () => {
  const router = useRouter();
  const [menuState, setMenuState] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleStartPractice = () => {
    router.push('/setup');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <nav
        data-state={menuState ? "active" : "inactive"}
        className={cn(
          "transition-all duration-300",
          scrolled
            ? "bg-gray-900/80 backdrop-blur-xl border-b border-white/10 shadow-lg"
            : "bg-transparent"
        )}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between py-4">
            {/* Logo */}
            <Link
              href="/"
              className="flex items-center space-x-2 group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white hidden sm:block">
                Practice Partner
              </span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              <ul className="flex gap-8">
                {menuItems.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={item.href}
                      className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>

              <Button
                onClick={handleStartPractice}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-semibold px-6 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-purple-500/50"
              >
                Start Free
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMenuState(!menuState)}
              aria-label={menuState ? "Close Menu" : "Open Menu"}
              className="lg:hidden relative z-20 p-2 text-white"
            >
              <Menu
                className={cn(
                  "w-6 h-6 transition-all duration-200",
                  menuState && "rotate-180 scale-0 opacity-0"
                )}
              />
              <X
                className={cn(
                  "w-6 h-6 absolute inset-0 m-auto transition-all duration-200",
                  menuState
                    ? "rotate-0 scale-100 opacity-100"
                    : "-rotate-180 scale-0 opacity-0"
                )}
              />
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          <div
            className={cn(
              "lg:hidden overflow-hidden transition-all duration-300",
              menuState ? "max-h-96 pb-6" : "max-h-0"
            )}
          >
            <div className="bg-gray-800/95 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <ul className="space-y-4 mb-6">
                {menuItems.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={item.href}
                      onClick={() => setMenuState(false)}
                      className="text-gray-300 hover:text-white block py-2 transition-colors duration-200 font-medium"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => {
                  handleStartPractice();
                  setMenuState(false);
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-semibold py-3 rounded-lg transition-all duration-200"
              >
                Start Free
              </Button>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};
